import { Link, useSearchParams } from 'react-router-dom';
import {
  getLearnCatalog,
  guideTabForSection,
  normalizeGuideTab,
  sectionsForGuideTab,
} from '../../lib/learnCatalog.js';
import { TERMS_URL, PRIVACY_URL } from '../../lib/legalTerms.js';

function linkifyBullet(text) {
  const urlMatch = String(text || '').match(/(https?:\/\/\S+)/);
  if (!urlMatch) return text;
  const url = urlMatch[1];
  const [before, after] = String(text).split(url);
  const isTerms = url === TERMS_URL;
  const isPrivacy = url === PRIVACY_URL;
  return (
    <>
      {before}
      <a href={url} target="_blank" rel="noopener noreferrer">
        {isTerms ? 'Open Terms of Service' : isPrivacy ? 'Open Privacy Policy' : url}
      </a>
      {after}
    </>
  );
}

function KindBadge({ kind }) {
  if (kind !== 'manual' && kind !== 'automated') return null;
  const label = kind === 'automated' ? 'Automated' : 'Manual';
  return (
    <span className={`learn-kind-badge learn-kind-badge--${kind}`} data-kind={kind}>
      {label}
    </span>
  );
}

function GuideSteps({ steps }) {
  if (!steps?.length) return null;
  return (
    <ol className="learn-steps">
      {steps.map((step, index) => (
        <li key={`${step.title}-${index}`} className="learn-step">
          <div className="learn-step-copy">
            <h3 className="learn-step-title">
              <span className="learn-step-index" aria-hidden="true">
                {index + 1}
              </span>
              {step.title}
            </h3>
            <p className="learn-step-body">{step.body}</p>
          </div>
          {step.image ? (
            <figure className="learn-step-figure">
              <img
                className="learn-step-image"
                src={step.image}
                alt={step.caption || step.title}
                loading="lazy"
              />
              {step.caption ? (
                <figcaption className="learn-step-caption">{step.caption}</figcaption>
              ) : null}
            </figure>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export default function LearnPage() {
  const catalog = getLearnCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get('section') || '';
  const activeTab = normalizeGuideTab(
    searchParams.get('tab') || (focus ? guideTabForSection(focus, catalog) : catalog.tabs[0].id)
  );
  const sections = sectionsForGuideTab(activeTab, catalog);

  function setTab(nextId) {
    const next = normalizeGuideTab(nextId);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    params.delete('section');
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="client-page learn-page">
      <div className="section-head">
        <div>
          <h1>{catalog.title}</h1>
          <p>{catalog.summary}</p>
        </div>
        <div className="section-head-actions">
          <Link className="btn-ghost" to="/client/settings?tab=billing">
            Open Billing
          </Link>
        </div>
      </div>

      <div className="record-tabs-wrap">
        <div className="record-tabs" role="tablist" aria-label="Guides sections">
          {catalog.tabs.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`guides-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`guides-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={selected ? 'record-tab active' : 'record-tab'}
                onClick={() => setTab(tab.id)}
              >
                <span className="record-tab-label">
                  <span className="record-tab-label-full">{tab.label}</span>
                  <span className="record-tab-label-short">{tab.shortLabel}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="record-tab-panels">
        <section
          id={`guides-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`guides-tab-${activeTab}`}
          className="learn-tab-panel"
        >
          <nav className="learn-toc" aria-label={`${catalog.tabs.find((t) => t.id === activeTab)?.label || 'Guides'} contents`}>
            <h2 className="learn-toc-title">Contents</h2>
            <ol className="learn-toc-list">
              {sections.map((section, index) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={
                      focus === section.id ? 'learn-toc-link is-active' : 'learn-toc-link'
                    }
                  >
                    <span className="learn-toc-index">{index + 1}</span>
                    <span className="learn-toc-link-text">
                      {section.title}
                      {section.kind ? (
                        <span className={`learn-toc-kind learn-toc-kind--${section.kind}`}>
                          {section.kind === 'automated' ? 'Automated' : 'Manual'}
                        </span>
                      ) : null}
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="learn-sections">
            {sections.map((section) => (
              <article
                key={section.id}
                id={section.id}
                className={
                  focus === section.id ? 'learn-section is-focused' : 'learn-section'
                }
              >
                <div className="learn-section-heading">
                  <h2>{section.title}</h2>
                  <KindBadge kind={section.kind} />
                </div>
                <p className="learn-section-body">{section.body}</p>
                <GuideSteps steps={section.steps} />
                {section.bullets?.length ? (
                  <ul className="learn-section-bullets">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{linkifyBullet(bullet)}</li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
